import { readConsent } from './consent';
import {
  captureGoogleAnalyticsEvent,
  captureGoogleAnalyticsPageView,
  initGoogleAnalytics,
} from './googleAnalytics';
import {
  captureMetaPixelEvent,
  captureMetaPixelPageView,
  initMetaPixel,
} from './metaPixel';
import {
  capturePosthogEvent,
  capturePosthogException,
  identifyPosthogUser,
  initAnonymousPosthog,
  initPosthog,
  registerPosthogProperties,
} from './posthog';

const safeDefaults = {
  app_surface: 'site',
};
const ATTRIBUTION_KEY = 'gc_session_attribution';

function getCurrentAttribution() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const attribution = {
    fbclid: params.get('fbclid') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_source: params.get('utm_source') || undefined,
    utm_term: params.get('utm_term') || undefined,
  };
  return Object.fromEntries(
    Object.entries(attribution).filter(([, value]) => Boolean(value)),
  );
}

function readStoredAttribution() {
  if (typeof window === 'undefined') return {};
  try {
    const stored = window.sessionStorage.getItem(ATTRIBUTION_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function persistAttribution(properties: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  const next = { ...readStoredAttribution(), ...properties };
  try {
    window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(next));
  } catch {
    return;
  }
}

function getAttributionProperties() {
  const current = getCurrentAttribution();
  if (Object.keys(current).length > 0) persistAttribution(current);
  return { ...readStoredAttribution(), ...current };
}

export async function initBaseTracking() {
  const [gaReady, metaReady] = await Promise.all([
    initGoogleAnalytics(),
    initMetaPixel(),
  ]);
  if (gaReady) captureGoogleAnalyticsPageView();
  if (metaReady) captureMetaPixelPageView();
}

export async function trackAnonymousPageView(
  pageName: string,
  properties: Record<string, unknown> = {},
) {
  if (readConsent() !== 'unset') return;
  const ready = await initAnonymousPosthog();
  if (!ready) return;
  capturePosthogEvent('page_viewed_anonymous', {
    ...safeDefaults,
    page_name: pageName,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...properties,
  });
}

async function ensureAnalytics() {
  if (readConsent() !== 'accepted') return false;
  const [posthogReady, gaReady, metaReady] = await Promise.all([
    initPosthog(),
    initGoogleAnalytics(),
    initMetaPixel(),
  ]);
  return Boolean(posthogReady || gaReady || metaReady);
}

export async function identifyUser(
  distinctId: string,
  properties: Record<string, unknown> = {},
) {
  const ready = await ensureAnalytics();
  if (!ready) return;
  identifyPosthogUser(distinctId, {
    ...getAttributionProperties(),
    ...properties,
  });
}

export async function trackEvent(
  name: string,
  properties: Record<string, unknown> = {},
) {
  const ready = await ensureAnalytics();
  if (!ready) return;
  const attribution = getAttributionProperties();
  const payload = { ...safeDefaults, ...attribution, ...properties };
  registerPosthogProperties(attribution, true);
  capturePosthogEvent(name, payload);
  captureGoogleAnalyticsEvent(name, payload);
  captureMetaPixelEvent(name, payload);
}

export async function trackPageView(
  pageName: string,
  properties: Record<string, unknown> = {},
) {
  await trackEvent('page_viewed', {
    page_name: pageName,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...properties,
  });
}

export async function trackScreenView(
  screenName: string,
  properties: Record<string, unknown> = {},
) {
  await trackEvent('screen_viewed', {
    app_surface: 'app',
    screen_name: screenName,
    ...properties,
  });
}

export function captureException(
  error: unknown,
  additionalProperties?: Record<string, unknown>,
) {
  capturePosthogException(error, additionalProperties);
}
